import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SearchInput } from "@/components/ui/search-input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TableStateRow } from "@/components/ui/table-state-row";
import { FilterMenu } from "@/components/ui/filter-menu";
import { useToast } from "@/components/ui/use-toast";
import { useDebounce } from "@/hooks/use-debounce";
import { useUrlListState } from "@/hooks/use-url-list-state";
import { queryKeys } from "@/lib/query-keys";
import { useFoodTypes } from "@/hooks/useNutritionEvaluation";
import { useTagMetadata } from "@/hooks/useNutritionDropdowns";
import { nutritionAPI } from "@/lib/nutrition-api";
import {
  buildFoodLockedReason,
  foodServingsEditable,
  type FoodNutrientBasis,
} from "@/lib/nutrition-form-utils";

import { FoodServingsCard } from "./FoodServingsCard";

interface FoodItemListItem {
  id: number;
  name: string;
  full_name: string;
  energy_kcal: string;
  // Identity fields. Two foods can share a name ("Almond" from IFCT and a
  // hand-entered one), and a serving attached to the wrong one is invisible
  // to the clinician who needed it — so the row has to say which is which.
  food_code?: string;
  source?: string;
  food_type?: string;
  food_type_display?: string;
}

interface SelectedFood {
  id: number;
  name: string;
  full_name: string;
  food_code?: string;
  source?: string;
  // Drives whether the servings form asks for a weight or a multiplier.
  // Read from the detail endpoint, not the search row, so it is never a guess.
  nutrient_basis: FoodNutrientBasis;
  // Server-owned `capabilities.can_edit_servings`. A reference-source food
  // (IFCT, USDA, USFDA) keeps editable servings, because a serving is Zarity's
  // own mapping and not the reference's data; only a retired food, which 404s,
  // is closed. Offering the buttons anyway would move that refusal to after
  // the click.
  can_edit: boolean;
  locked_reason?: string;
}

const PAGE_SIZE = 50;

/**
 * Standalone Food Servings tab.
 *
 * Lifted out of UpdateFoodEntryForm so serving CRUD has its own surface.
 * The user picks a food (canonical SearchInput pattern) and the
 * existing FoodServingsCard handles the rest.
 *
 * The picker mirrors RecipeFoodPickerDialog — food-type tabs, source chips,
 * and a Source column — because it is the same job (find the one food I mean)
 * and this screen is where getting it wrong is least visible.
 */
export const FoodServings = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  // The search and filters live in the URL, and so does the picked food
  // (?food=<id>, pushed onto history). Browser back or "Change food" returns to
  // the same results instead of an empty search.
  const [listState, setListState] = useUrlListState({
    q: "",
    type: [] as string[],
    source: [] as string[],
  });
  const searchInput = listState.q;
  const foodTypeFilter = listState.type;
  const sourceFilter = listState.source;
  const setSearchInput = (q: string) => setListState({ q });
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedFoodId = Number(searchParams.get("food")) || null;
  const [selectedFood, setSelectedFood] = useState<SelectedFood | null>(null);
  const [isLoadingFood, setIsLoadingFood] = useState(false);

  const { data: foodTypes = [] } = useFoodTypes();
  const { data: tagMetadata } = useTagMetadata();

  // Canonical source list from tag-metadata, not derived from the current page
  // of results — a chip has to be able to widen the search, not just re-filter
  // what a name match happened to return.
  const sourceOptions = useMemo(
    () =>
      (tagMetadata?.sources_and_brands.food_sources ?? [])
        .map((s) => s.name)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ value: name, label: name })),
    [tagMetadata]
  );
  // Both filters are multi-select; the backend takes each comma-separated.
  const foodTypeParam = foodTypeFilter.join(",") || undefined;
  const sourceParam = sourceFilter.join(",") || undefined;
  const hasActiveFilters = foodTypeFilter.length > 0 || sourceFilter.length > 0;
  const toggleIn = (value: string) => (current: string[]) =>
    current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];

  // The list row does not carry nutrient_basis, and the servings form cannot
  // be rendered honestly without it — fetch the detail before showing the card.
  const setFoodParam = (foodId: number | null, replace: boolean) =>
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (foodId) next.set("food", String(foodId));
        else next.delete("food");
        return next;
      },
      { replace }
    );
  const handleSelectFood = (item: FoodItemListItem) =>
    setFoodParam(item.id, false);
  const clearSelectedFood = () => setFoodParam(null, true);

  useEffect(() => {
    if (!selectedFoodId) {
      setSelectedFood(null);
      return;
    }
    if (selectedFood?.id === selectedFoodId) return;
    let cancelled = false;
    setIsLoadingFood(true);
    nutritionAPI.foodItems
      .get(selectedFoodId)
      .then((details: any) => {
        if (cancelled) return;
        // Default true so a payload without capabilities doesn't wrongly lock
        // the table — same default as ViewFood.
        const canEdit = foodServingsEditable(details?.capabilities);
        setSelectedFood({
          id: selectedFoodId,
          name: details?.name ?? "",
          full_name: details?.full_name,
          food_code: details?.food_code,
          source: details?.source,
          nutrient_basis: details?.nutrient_basis ?? "100G",
          can_edit: canEdit,
          locked_reason: canEdit
            ? undefined
            : buildFoodLockedReason(details, details?.name, "servings"),
        });
      })
      .catch((error: any) => {
        if (cancelled) return;
        // Without the basis the servings form would ask for the wrong field, so
        // stay on the search rather than opening it on a guess.
        toast({
          title: "Error",
          description:
            error?.message || "Failed to load this food item. Try again.",
          variant: "destructive",
        });
        clearSelectedFood();
      })
      .finally(() => {
        if (!cancelled) setIsLoadingFood(false);
      });
    return () => {
      cancelled = true;
    };
    // Load only when the picked food changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFoodId]);

  // A name is always required. The filters narrow a search; they do not browse
  // the catalog on their own — ?source=IFCT with no search term is a 542-row
  // scan that measured ~8s against dev, where the same query with a name takes
  // ~300ms. Until the backend can page that cheaply, we do not fire it.
  const debouncedSearch = useDebounce(searchInput.trim(), 300);
  const hasQuery = debouncedSearch.length >= 2;

  // Search and filters are linked: results match the text AND the chosen types
  // and sources. Keyed by all three, so a reply for an older combination can
  // never overwrite the current one (the old effect let a slow unfiltered
  // reply land after the filtered one).
  const listParams = {
    search: debouncedSearch,
    food_type: foodTypeParam,
    source: sourceParam,
    page_size: PAGE_SIZE,
  };
  const {
    data: searchData,
    isFetching: isSearching,
    isError: searchFailed,
    refetch: retrySearch,
  } = useQuery({
    queryKey: queryKeys.nutrition.foodItems.list(listParams),
    queryFn: () => nutritionAPI.foodItems.list(listParams),
    enabled: hasQuery,
  });
  const foodItems: FoodItemListItem[] =
    hasQuery && !isSearching ? (searchData?.results ?? []) : [];
  const totalCount = searchData?.count ?? foodItems.length;

  const isTruncated = totalCount > foodItems.length;

  return (
    <div className="h-full flex flex-col min-h-0 gap-4">
      <Card className="max-w-[2000px] flex-1 flex flex-col min-h-0 overflow-visible">
        <CardHeader className="shrink-0">
          <CardTitle className="z-h2">Food Servings</CardTitle>
          <p className="z-caption text-muted-foreground">
            Manage serving units for a food item — pick the food, then add or
            edit its servings.
          </p>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto pt-2 pb-6">
          {!selectedFoodId ? (
            <div className="flex h-full min-h-0 flex-col gap-3">
              <SearchInput
                placeholder="Search a food by name or code..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onClear={() => setSearchInput("")}
                isLoading={isSearching}
                containerClassName="w-full md:max-w-md"
              />

              <div className="flex flex-wrap items-center gap-2">
                <FilterMenu
                  label="Type"
                  options={foodTypes}
                  value={foodTypeFilter}
                  onValueChange={(v) =>
                    setListState({ type: toggleIn(v)(foodTypeFilter) })
                  }
                  multiple
                />
                <FilterMenu
                  label="Source"
                  options={sourceOptions}
                  value={sourceFilter}
                  onValueChange={(v) =>
                    setListState({ source: toggleIn(v)(sourceFilter) })
                  }
                  multiple
                />
                {hasActiveFilters && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-auto"
                    onClick={() => {
                      setListState({ type: [], source: [] });
                    }}
                  >
                    Reset
                  </Button>
                )}
              </div>

              {hasQuery && (
                <>
                  <div className="border rounded-lg min-h-0 flex-1 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="hover:bg-transparent">
                          <TableHead className="w-[120px] z-eyebrow text-muted-foreground sticky top-0 z-10 bg-card">
                            Food Code
                          </TableHead>
                          <TableHead className="z-eyebrow text-muted-foreground sticky top-0 z-10 bg-card">
                            Food Name
                          </TableHead>
                          <TableHead className="w-[120px] z-eyebrow text-muted-foreground sticky top-0 z-10 bg-card">
                            Type
                          </TableHead>
                          <TableHead className="w-[160px] z-eyebrow text-muted-foreground sticky top-0 z-10 bg-card">
                            Source
                          </TableHead>
                          <TableHead className="w-[120px] text-right z-eyebrow text-muted-foreground sticky top-0 z-10 bg-card">
                            Energy
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {isSearching ? (
                          <TableStateRow
                            colSpan={5}
                            state="loading"
                            message="Searching…"
                          />
                        ) : searchFailed ? (
                          <TableStateRow
                            colSpan={5}
                            state="error"
                            message="Couldn't search foods — the server didn't answer in time."
                            onRetry={() => retrySearch()}
                          />
                        ) : foodItems.length === 0 ? (
                          <TableStateRow
                            colSpan={5}
                            state="empty"
                            message={
                              hasActiveFilters
                                ? "No food items found. Try another term, or clear the type and source filters."
                                : "No food items found. Try another term."
                            }
                          />
                        ) : null}
                        {foodItems.map((item) => (
                          <TableRow
                            key={item.id}
                            className="cursor-pointer hover:bg-muted/40"
                            aria-disabled={isLoadingFood}
                            onClick={() => {
                              if (!isLoadingFood) handleSelectFood(item);
                            }}
                          >
                            <TableCell>
                              {item.food_code ? (
                                <Badge
                                  variant="neutral"
                                  className="font-mono tabular-nums"
                                >
                                  {item.food_code}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="z-body whitespace-normal">
                              <div className="break-words">{item.name}</div>
                              {item.full_name &&
                                item.full_name !== item.name && (
                                  <div className="z-caption text-muted-foreground break-words">
                                    {item.full_name}
                                  </div>
                                )}
                            </TableCell>
                            <TableCell>
                              {item.food_type_display || item.food_type ? (
                                <Badge variant="neutral">
                                  {item.food_type_display || item.food_type}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {item.source && item.source.trim() ? (
                                <Badge variant="secondary">{item.source}</Badge>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="z-caption text-muted-foreground text-right">
                              {item.energy_kcal} kcal
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {isTruncated && (
                    <p className="z-caption text-muted-foreground">
                      Showing the first {foodItems.length} of {totalCount}{" "}
                      matches — narrow the search or filter by source.
                    </p>
                  )}
                </>
              )}
              {!hasQuery && (
                <p className="z-caption text-muted-foreground">
                  Search for the food whose servings you want to manage. Type
                  and source narrow the search.
                </p>
              )}
            </div>
          ) : !selectedFood ? (
            <p className="z-caption text-muted-foreground">Loading food…</p>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="z-label text-muted-foreground">Food item</div>
                  <div className="z-h3 truncate">{selectedFood.name}</div>
                  {selectedFood.full_name &&
                    selectedFood.full_name !== selectedFood.name && (
                      <div className="z-caption text-muted-foreground truncate">
                        {selectedFood.full_name}
                      </div>
                    )}
                  {/* The same identity the picker showed, so a wrong pick is
                      still visible after the list is gone. */}
                  <div className="mt-1 flex items-center gap-2">
                    {selectedFood.food_code && (
                      <span className="z-caption text-muted-foreground">
                        {selectedFood.food_code}
                      </span>
                    )}
                    {selectedFood.source && selectedFood.source.trim() && (
                      <Badge variant="secondary">{selectedFood.source}</Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      navigate(
                        `/settings/nutrition/view-food/${selectedFood.id}`
                      )
                    }
                  >
                    View food
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={clearSelectedFood}
                  >
                    Change food
                  </Button>
                </div>
              </div>
              <FoodServingsCard
                foodItemId={selectedFood.id}
                foodItemName={selectedFood.name}
                nutrientBasis={selectedFood.nutrient_basis}
                canEdit={selectedFood.can_edit}
                lockedReason={selectedFood.locked_reason}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FoodServings;
